/**
 * Credential Testing View
 *
 * Shows the testing, success, and failure states for credential login tests.
 * Used inside the CredentialDialogWithTest component.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrowserStreamPanel, Button, Text, Heading, Flex, Box, Spinner } from '@chatsdet/ui';
import { Check, Warning, ArrowSquareOut, X } from '@phosphor-icons/react';
import type { BrowserStreamState } from '@chatsdet/types';
import { useNavigate } from '@tanstack/react-router';
import type { DialogPhase } from './useCredentialDialogState';

type CredentialTestingViewProps = {
  phase: DialogPhase;
  credentialName: string;
  browserStreamState: BrowserStreamState;
  errorMessage?: string | null;
  loginWorkflowCreated?: { workflowId: string; workflowName: string } | null;
  isApiLogin?: boolean;
  onCancelTest: () => void;
  onRetry: () => void;
  onSkip: () => void;
  onContinue: () => void;
};

/**
 * Testing view - shows live browser preview
 */
function TestingView({
  credentialName,
  browserStreamState,
  onCancelTest,
}: {
  credentialName: string;
  browserStreamState: BrowserStreamState;
  onCancelTest: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-w-0 space-y-4"
    >
      <Box className="mb-4">
        <Heading level={3} className="mb-1">
          Testing Login for "{credentialName}"
        </Heading>
        <Text size="sm" className="text-muted-foreground">
          Verifying your credentials work correctly...
        </Text>
      </Box>

      <BrowserStreamPanel
        state={browserStreamState}
        layout="split"
        screenshotAspectRatio="aspect-video"
        showActionHistory={true}
        showReasoningPanel={true}
        emptyScreenshotLabel="Starting browser..."
        emptyReasoningLabel="Preparing login test..."
        allowExpand={true}
        showHeader={true}
      />

      <Flex justify="end" className="-mx-6 -mb-6 mt-6 border-t border-border bg-card px-6 py-4">
        <Button variant="secondary" iconLeft={<X size={16} />} onPress={onCancelTest}>
          Cancel Test
        </Button>
      </Flex>
    </motion.div>
  );
}

/**
 * Shared reasoning panel for API Login views
 */
function ApiLoginReasoningPanel({
  browserStreamState,
}: {
  browserStreamState: BrowserStreamState;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const reasoning = browserStreamState.reasoning || '';
  const thought = browserStreamState.latestThought || '';

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [reasoning, thought]);

  return (
    <Box
      ref={scrollRef}
      className="h-48 overflow-y-auto rounded-lg border border-border bg-muted/30 p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap"
    >
      {thought && (
        <Box className="mb-2 pb-2 border-b border-border/50">
          <Text size="xs" className="text-primary font-semibold">
            Latest:
          </Text>
          <Text size="xs" className="text-foreground">
            {thought}
          </Text>
        </Box>
      )}
      {reasoning ? (
        <Text size="xs" className="text-muted-foreground">
          {reasoning}
        </Text>
      ) : (
        <Text size="xs" className="text-muted-foreground italic">
          Waiting for agent response...
        </Text>
      )}
    </Box>
  );
}

/**
 * API Login testing view - shows agent reasoning instead of browser screenshots
 */
function ApiLoginTestingView({
  credentialName,
  browserStreamState,
  onCancelTest,
}: {
  credentialName: string;
  browserStreamState: BrowserStreamState;
  onCancelTest: () => void;
}) {
  const status = browserStreamState.status;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-w-0 space-y-4"
    >
      <Box className="mb-4">
        <Heading level={3} className="mb-1">
          Testing API Login for &quot;{credentialName}&quot;
        </Heading>
        <Text size="sm" className="text-muted-foreground">
          Calling token endpoint and discovering token configuration...
        </Text>
      </Box>

      {/* Status indicator */}
      <Flex
        align="center"
        gap="sm"
        className="px-3 py-2 rounded-lg bg-muted/50 border border-border"
      >
        {status === 'connected' || status === 'connecting' ? (
          <Spinner size="sm" />
        ) : status === 'disconnected' ? (
          <Check size={16} className="text-green-500" />
        ) : null}
        <Text size="sm" className="text-muted-foreground">
          {status === 'connecting' && 'Connecting...'}
          {status === 'connected' && 'Testing token endpoint...'}
          {status === 'disconnected' && 'Test complete'}
        </Text>
      </Flex>

      <ApiLoginReasoningPanel browserStreamState={browserStreamState} />

      <Flex justify="end" className="-mx-6 -mb-6 mt-6 border-t border-border bg-card px-6 py-4">
        <Button variant="secondary" iconLeft={<X size={16} />} onPress={onCancelTest}>
          Cancel Test
        </Button>
      </Flex>
    </motion.div>
  );
}

/**
 * API Login failure view - keeps the reasoning panel visible with error + retry buttons
 */
function ApiLoginFailureView({
  browserStreamState,
  errorMessage,
  onRetry,
  onSkip,
}: {
  browserStreamState: BrowserStreamState;
  errorMessage?: string | null;
  onRetry: () => void;
  onSkip: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-w-0 space-y-4"
    >
      <Flex align="center" gap="md" className="mb-4">
        <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
          <Warning size={24} weight="fill" className="text-red-500" />
        </div>
        <div>
          <Heading level={3}>API Login Failed</Heading>
          <Text size="sm" className="text-muted-foreground">
            Could not obtain a token from the endpoint
          </Text>
        </div>
      </Flex>

      <ApiLoginReasoningPanel browserStreamState={browserStreamState} />

      {errorMessage && (
        <Box className="max-h-48 overflow-auto rounded border border-red-500/20 bg-red-500/10 p-3">
          <Text size="sm" className="break-all whitespace-pre-wrap font-mono text-red-500">
            {errorMessage}
          </Text>
        </Box>
      )}

      <Flex
        justify="end"
        gap="sm"
        className="-mx-6 -mb-6 mt-6 border-t border-border bg-card px-6 py-4"
      >
        <Button variant="secondary" onPress={onSkip}>
          Skip
        </Button>
        <Button variant="primary" onPress={onRetry}>
          Edit & Retry
        </Button>
      </Flex>
    </motion.div>
  );
}

/**
 * Success view - animated checkmark with auto-close option
 */
function SuccessView({
  loginWorkflowCreated,
  onContinue,
}: {
  loginWorkflowCreated?: { workflowId: string; workflowName: string } | null;
  onContinue: () => void;
}) {
  const navigate = useNavigate();

  // Auto-close after 2 seconds (only if no workflow link to show)
  React.useEffect(() => {
    if (loginWorkflowCreated) return;
    const timer = setTimeout(() => {
      onContinue();
    }, 2000);
    return () => clearTimeout(timer);
  }, [onContinue, loginWorkflowCreated]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="flex flex-col items-center justify-center py-12 space-y-6"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 15 }}
        className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
        >
          <Check size={48} weight="bold" className="text-green-500" />
        </motion.div>
      </motion.div>

      <div className="text-center space-y-2">
        <Heading level={3}>Login Successful</Heading>
        <Text size="sm" className="text-muted-foreground">
          Your credential is ready to use
        </Text>
      </div>

      {loginWorkflowCreated && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center"
        >
          <Button
            variant="ghost"
            onPress={() => {
              onContinue();
              void navigate({
                to: '/flows/$workflowId',
                params: { workflowId: loginWorkflowCreated.workflowId },
              });
            }}
          >
            <ArrowSquareOut size={16} />
            View Login Workflow
          </Button>
        </motion.div>
      )}

      <Flex justify="center" className="pt-4">
        <Button variant="primary" onPress={onContinue}>
          Continue
        </Button>
      </Flex>
    </motion.div>
  );
}

/**
 * Failure view - shows error with options to retry or skip
 */
function FailureView({
  browserStreamState,
  errorMessage,
  onRetry,
  onSkip,
}: {
  browserStreamState: BrowserStreamState;
  errorMessage?: string | null;
  onRetry: () => void;
  onSkip: () => void;
}) {
  // Get the last screenshot if available
  const lastScreenshot = browserStreamState.latestScreenshot;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-w-0 space-y-4"
    >
      <Flex align="center" gap="md" className="mb-4">
        <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
          <Warning size={24} weight="fill" className="text-red-500" />
        </div>
        <div>
          <Heading level={3}>Login Failed</Heading>
          <Text size="sm" className="text-muted-foreground">
            Could not complete the login test
          </Text>
        </div>
      </Flex>

      {/* Show last screenshot if available */}
      {lastScreenshot && (
        <Box className="rounded-lg border border-border overflow-hidden">
          <img
            src={
              lastScreenshot.imageBase64.startsWith('data:')
                ? lastScreenshot.imageBase64
                : `data:image/png;base64,${lastScreenshot.imageBase64}`
            }
            alt="Last screenshot from test"
            className="w-full aspect-video object-contain bg-muted"
          />
        </Box>
      )}

      {/* Error message */}
      <Box className="p-4 rounded-lg bg-muted/50 border border-border">
        <Text size="sm" className="text-muted-foreground">
          Could not complete login. You can update your credentials and try again, or skip for now.
        </Text>
        {errorMessage && (
          <Box className="mt-3 max-h-48 overflow-auto rounded border border-red-500/20 bg-red-500/10 p-3">
            <Text size="sm" className="break-all whitespace-pre-wrap font-mono text-red-500">
              {errorMessage}
            </Text>
          </Box>
        )}
      </Box>

      <Flex
        justify="end"
        gap="sm"
        className="-mx-6 -mb-6 mt-6 border-t border-border bg-card px-6 py-4"
      >
        <Button variant="secondary" onPress={onSkip}>
          Skip
        </Button>
        <Button variant="primary" onPress={onRetry}>
          Edit & Retry
        </Button>
      </Flex>
    </motion.div>
  );
}

/**
 * Saving view - shows loading state while saving credential
 */
function SavingView() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center py-12 space-y-4"
    >
      <Spinner size="lg" />
      <Text size="sm" className="text-muted-foreground">
        Saving credential...
      </Text>
    </motion.div>
  );
}

/**
 * Main component - renders the appropriate view based on phase
 */
export function CredentialTestingView({
  phase,
  credentialName,
  browserStreamState,
  errorMessage,
  loginWorkflowCreated,
  isApiLogin,
  onCancelTest,
  onRetry,
  onSkip,
  onContinue,
}: CredentialTestingViewProps) {
  return (
    <AnimatePresence mode="wait">
      {phase === 'saving' && <SavingView key="saving" />}
      {phase === 'testing' &&
        (isApiLogin ? (
          <ApiLoginTestingView
            key="api-login-testing"
            credentialName={credentialName}
            browserStreamState={browserStreamState}
            onCancelTest={onCancelTest}
          />
        ) : (
          <TestingView
            key="testing"
            credentialName={credentialName}
            browserStreamState={browserStreamState}
            onCancelTest={onCancelTest}
          />
        ))}
      {phase === 'success' && (
        <SuccessView
          key="success"
          loginWorkflowCreated={loginWorkflowCreated}
          onContinue={onContinue}
        />
      )}
      {phase === 'failure' &&
        (isApiLogin ? (
          <ApiLoginFailureView
            key="api-login-failure"
            browserStreamState={browserStreamState}
            errorMessage={errorMessage}
            onRetry={onRetry}
            onSkip={onSkip}
          />
        ) : (
          <FailureView
            key="failure"
            browserStreamState={browserStreamState}
            errorMessage={errorMessage}
            onRetry={onRetry}
            onSkip={onSkip}
          />
        ))}
    </AnimatePresence>
  );
}
